import React, { useState } from 'react';
import { Box } from '@mui/material';
import {
  Breadcrumb,
  createSearchState,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  FormSearchResultAdvanced,
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
  FormUrlDataProcessor,
} from '@ui-framework/index';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  getDefaultValueByControl,
} from '@product-framework/index';
import {
  fileName,
  searchCriteria,
  createdDateTime,
  expirationDateTime,
  status,
  failedReason,
  totalRecords,
  download,
  preview,
  exportType,
  fileSize,
} from './accessorFunctions';
import { listSchema, searchSchema } from '../../schemas';
import validationSchema from './validations';
import { exportTypeOptions } from '../../exportTypeConfig';

const STORE_NAME = 'DataExportHistoryList';

function DataExportHistoryList() {
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const store = useStore();

  const [searchUrlParams] = useSearchParams();
  const idFromUrl = searchUrlParams.get('id');

  const [showSearch, setShowSearch] = useState(false);
  const [urlSeedReady, setUrlSeedReady] = useState(!idFromUrl);
  const [resultsFound, setResultsFound] = useState(null);

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);

  const listApiUrl = `${dataProvider.getApiUrl()}/sxcl/v1/data-export-history`;
  const navigateToDataExportHistory = () => {
    window.location.href = `/data-export-history`;
  };

  // Resolve ?id= to fileName/exportType before ADVANCED_LIST mounts so URL params don't race a default fetch
  const pageOnLoad = async () => {
    if (!idFromUrl) {
      return { errors: null };
    }
    const response = await dataProvider.get(`${listApiUrl}/${idFromUrl}`);
    if (response?.ok) {
      store.domain.list.set(
        STORE_NAME,
        createSearchState(
          {
            fileName: response.data?.fileName,
            exportType: response.data?.exportType,
          },
          searchSchema,
        ),
      );
      setUrlSeedReady(true);
      navigateToDataExportHistory();
    }
    return { errors: response?.errors };
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'EXPORT_HISTORY',
        defaultMessage: 'Export History',
      }),
      icon: IconLibrary.ServiceJobExport,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: !showSearch
        ? formatMessage({
            id: 'SHOW_SEARCH',
            defaultMessage: 'Show Search',
          })
        : formatMessage({
            id: 'HIDE_SEARCH',
            defaultMessage: 'Hide Search',
          }),
      Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
      variant: 'contained',
      onClick: () => {
        setShowSearch(!showSearch);
      },
    },
  ];

  const preProcessSearchRequestData = data => {
    const { id: idArg, ...other } = data;
    return Object.fromEntries(
      Object.entries({
        ...other,
      }).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
  };

  const submitSearchForm = async data => {
    const requestData = preProcessSearchRequestData(data);
    const searchState = createSearchState(requestData, searchSchema);
    store.domain.list.set(STORE_NAME, searchState);
  };

  const preProcessSearchValues = searchedParams => {
    return searchedParams;
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: STORE_NAME,
    dataProvider,
    schema: listSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    accessorFunctions: {
      fileName,
      searchCriteria,
      createdDateTime,
      expirationDateTime,
      status,
      failedReason,
      totalRecords,
      download,
      preview,
      exportType,
      fileSize,
    },
    accessorFunctionsAdditional: {
      getApiUrl: () => dataProvider.getApiUrl(),
      get: (...args) => dataProvider.get(...args),
      formatMessage,
      snackbar,
    },
    onRowCountChange: rowCount => {
      setResultsFound(rowCount);
    },
  };

  const searchProps = {
    customComponents: FormFields,
    storeName: STORE_NAME,
    IconLibrary,
    schema: searchSchema,
    onSubmit: submitSearchForm,
    onClear: () => {
      store.domain.list.reset(STORE_NAME);
    },
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => {
      setShowSearch(false);
    },
    getValueByControl: getDefaultValueByControl,
  };

  const searchValueFormatter = ({ name: searchName, value }) => {
    switch (searchName) {
      case 'exportType': {
        const found = exportTypeOptions.find(opt => opt.value === value);
        return found?.label ?? value;
      }
      default:
        return null;
    }
  };

  const searchParamsProps = {
    preProcessSearchValues,
    storeName: STORE_NAME,
    schema: searchSchema,
    resultsFound,
    valueFormatter: searchValueFormatter,
  };

  return (
    <Layout>
      <FormUrlDataProcessor
        onLoad={() => pageOnLoad()}
        onError={() => {
          navigateToDataExportHistory();
        }}>
        <Grid2 xs={12} container>
          <Grid2 xs={12} md={6}>
            <Breadcrumb items={breadCrumb} />
          </Grid2>
          <Grid2 xs={12} md={6}>
            <StackablePageButtons buttons={stackablePageButtons} />
          </Grid2>
        </Grid2>
        <Grid2 xs={12}>
          <FormSearchResultAdvanced {...searchParamsProps} />
        </Grid2>

        {showSearch && (
          <PageSection
            containerProps={{ sx: { mb: 3 } }}
            label={formatMessage({
              id: 'SEARCH_EXPORT_HISTORY',
              defaultMessage: 'Search Export History',
            })}>
            <Box>
              <CRUDComponent
                type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
                searchProps={searchProps}
              />
            </Box>
          </PageSection>
        )}

        <PageSection
          label={formatMessage({
            id: 'EXPORT_HISTORY',
            defaultMessage: 'Export History',
          })}>
          <Box>
            {urlSeedReady && (
              <CRUDComponent
                type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
                listProps={listProps}
              />
            )}
          </Box>
        </PageSection>
      </FormUrlDataProcessor>
    </Layout>
  );
}

export default observer(DataExportHistoryList);
